import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import CollapsibleSimple from 'components/collapsible/simple/CollapsibleSimple';
import SearchableDropdown from 'components/inputs/searchable-dropdown/SearchableDropdown';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import ControlledRangeSelect from 'modules/discovery/components/range-select/ControlledRangeSelect';
import RangeSelectorTitle from 'modules/discovery/creators/components/filters/range-selector-header/RangeSelectorTitle';
import DiscoveryCreatorsFilterPopover from 'modules/freemium/components/discovery-creators-filter-popover/DiscoveryCreatorsFilterPopover';

import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import ActionLink from 'components/buttons/action-link/ActionLink';
import CollapsibleContainer from 'components/collapsible/container/CollapsibleContainer';

import { dataCommonToComponent, dataComponentToCommon } from 'utils/data/data';
import { __t, applyTranslations } from 'utils/language';
import { countrySearchToDropdownItems, wrappedCitySearchToDropdownItems } from 'utils/locations';
import { getProfileStatus } from 'modules/discovery/utils/discovery';
import { UNVERIFIED_DISABLED_PROPS } from 'modules/discovery/creators/utils/creators';

import './discovery-creators-filters-creator-details.scss';

import type {
  Data,
  DataStructureCommon,
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilters,
  DiscoveryFilterCheckbox,
  DiscoveryFilterMinMax,
  MixedSelectDropdownItem
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./discovery-creators-filters-creator-details.i18n.json'));

type OwnProps = {
  filters: DiscoveryCreatorsFilters,
  isFreemium: boolean,
  lastStateReset: number,
  handleDiscoveryFiltersUpdate: (a: DiscoveryCreatorsFilter) => void
};

type DispatchProps = {
  dispatchSearchCities: (a: string) => Promise<Array<MixedSelectDropdownItem>>
};

type Props = OwnProps & DispatchProps;

type State = {
  showAllFilters: boolean
};

class DiscoveryCreatorsFiltersCreatorDetails extends React.PureComponent<Props, State> {
  static defaultProps: OwnProps = {
    filters: {} as DiscoveryCreatorsFilters,
    isFreemium: false,
    lastStateReset: Date.now(),
    handleDiscoveryFiltersUpdate: () => {}
  };

  state = {
    showAllFilters: false
  };

  isLightweightSelected(): boolean {
    const { filters } = this.props;
    const profileStatus = getProfileStatus(filters.profiles, 'creators');

    return profileStatus.unverified;
  }

  isSectionDisabled(identifier: string): boolean {
    const { isFreemium } = this.props;

    return (this.isLightweightSelected() && UNVERIFIED_DISABLED_PROPS.includes(identifier))
      || (isFreemium && ['bodyAttributes', 'ethnicGroups', 'professions'].includes(identifier));
  }

  handleUpdate = (result: DiscoveryCreatorsFilter): void => {
    const { handleDiscoveryFiltersUpdate } = this.props;

    if (handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate(result);
    }
  };

  // ===== Age
  handleSliderChangeComplete = (range: DiscoveryFilterMinMax): void => {
    const { age } = this.props.filters;

    this.handleUpdate({
      age: {
        ...age,
        ...range
      }
    });
  };

  // ====== Gender
  handleCheckboxChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { filters } = this.props;
    const target = event.currentTarget;
    const name = target.name;
    const isChecked = target.checked;

    if (name) {
      const arr = filters.genders;
      const selectedItem = arr.find(item => item.identifier === name);

      const updatedFilters = arr.map(item => {
        if (selectedItem && item.identifier === selectedItem.identifier) {
          return {
            ...selectedItem,
            selected: isChecked
          };
        }

        return item;
      });

      this.handleUpdate({
        genders: updatedFilters
      });
    }
  };

  // ===== Mixed Select Dropdown
  handleItemsUpdate = (updatedItems: Array<MixedSelectDropdownItem>, data?: Data | null): void => {
    const { filters } = this.props;

    if (!data || !data.name) {
      return;
    }

    const name = data.name;
    this.handleUpdate({
      [name]: filters[name].map(entry => ({
        ...entry,
        selected: (
          updatedItems.find((item) => item.identifier === entry.identifier) || {}
        ).isSelected
      }))
    });
  };

  handleExtraFiltersToggle = (): void => {
    this.setState((state) => ({
      showAllFilters: !state.showAllFilters
    }));
  };

  // ===== Locations
  handleLocationChange = (items: Array<MixedSelectDropdownItem>, data?: Data | null) => {
    if (!data || !data.name) {
      return;
    }

    this.handleUpdate({
      [data.name]: items.filter((item) => item.isSelected).map(dataComponentToCommon)
    });
  };

  renderTitle(title: string, identifier: string): React.ReactNode {
    return (
      <RangeSelectorTitle
        title={title}
        isDisabled={this.isSectionDisabled(identifier)}
      />
    );
  }

  renderFilterPopover(children: React.ReactNode): React.ReactNode {
    return (
      <DiscoveryCreatorsFilterPopover
        triggerComponent={children}
      />
    );
  }

  renderAgeSlider() {
    const { age } = this.props.filters;

    return (
      <ControlledRangeSelect
        key="creator-age"
        title={__t('discovery_creators_filters_creator_details.age_title')}
        data={age}
        isDisabled={this.isSectionDisabled('age')}
        onChangeComplete={this.handleSliderChangeComplete}
      />
    );
  }

  renderCheckbox(values: DiscoveryFilterCheckbox): React.ReactNode {
    return (
      <InputCheckbox
        key={`discovery-creators-filters-general__checkbox-${values.identifier}`}
        id={`discovery-creators-filters-general__checkbox-${values.identifier}`}
        name={String(values.name)}
        label={values.label}
        value={String(values.value)}
        checked={values.checked}
        onChange={this.handleCheckboxChange}
      />
    );
  }

  renderGender() {
    const { genders } = this.props.filters;

    return (
      <React.Fragment>
        {this.renderTitle(__t('discovery_creators_filters_creator_details.gender_title'), 'genders')}
        <div className="discovery-creators-filters-creator-details__genders">
          {
            (genders || []).map(gender => (
              <div
                key={`discovery-creators-filters-general__${gender.identifier}`}
                className="discovery-creators-filters-general__section-checkbox"
              >
                {
                  this.renderCheckbox({
                    identifier: gender.identifier,
                    name: gender.identifier,
                    label: gender.label,
                    value: gender.selected,
                    checked: gender.selected,
                    parent: 'genders'
                  })
                }
              </div>
            ))
          }
        </div>
      </React.Fragment>
    );
  }

  renderLocation(
    key: 'cities' | 'countries',
    dataFetcher: (a: string) => Promise<Array<MixedSelectDropdownItem>>
  ): React.ReactNode {
    return (
      <React.Fragment>
        {this.renderTitle(
          __t(`discovery_creators_filters_creator_details.${key}_title`),
          key
        )}
        <VerticalSpacing type="small" />
        <SearchableDropdown
          key={`discovery-creators-filters-content__creator-${key}-${this.props.lastStateReset}`}
          withSearchable={false}
          items={this.props.filters[key].map(dataCommonToComponent)}
          dataFetcher={dataFetcher}
          data={{
            name: key
          }}
          onItemsUpdate={this.handleLocationChange}
        />
      </React.Fragment>
    );
  }

  renderMixedDropdown(key: string, data: Array<MixedSelectDropdownItem>, isDisabled: boolean): React.ReactNode {
    return (
      <MixedSelectDropdown
        key={`discovery-creators-filters-content__creator-${key}-${this.props.lastStateReset}`}
        name={`discovery-creators-filters-content__creator-${key}-${this.props.lastStateReset}`}
        items={data}
        withMulti
        withRemovable
        withSortable={key !== 'ethnicGroups'}
        withCheckboxes
        isDisabled={isDisabled}
        onItemsToggle={this.handleItemsUpdate}
        onItemsRemove={this.handleItemsUpdate}
        onItemsRemoveAll={this.handleItemsUpdate}
        data={{
          name: key
        }}
      />
    );
  }

  renderDropdown(key: string, data: Array<DataStructureCommon>, isDisabled: boolean): React.ReactNode {
    const { isFreemium } = this.props;
    const mappedData = data.map(dataCommonToComponent);

    return (
      <React.Fragment>
        {
          this.renderTitle(
            __t(`discovery_creators_filters_creator_details.${key}_title`),
            key
          )
        }
        <VerticalSpacing type="small" />
        {
          isDisabled && isFreemium
            ? this.renderFilterPopover(this.renderMixedDropdown(key, mappedData, isDisabled))
            : this.renderMixedDropdown(key, mappedData, isDisabled)
        }
      </React.Fragment>
    );
  }

  renderExtraFilters() {
    const { filters, dispatchSearchCities } = this.props;
    const { bodyAttributes, ethnicGroups, professions } = filters;
    const { showAllFilters } = this.state;

    const message = (showAllFilters)
      ? __t('discovery_creators_filters_creator_details.hide_extra_filters')
      : __t('discovery_creators_filters_creator_details.show_extra_filters');

    return (
      <React.Fragment>
        <ActionLink
          size="normal"
          onClick={this.handleExtraFiltersToggle}
        >
          {message}
        </ActionLink>
        <CollapsibleContainer
          isExpanded={showAllFilters}
        >
          <VerticalSpacing type="large" />
          {this.renderLocation('cities', dispatchSearchCities)}
          <VerticalSpacing type="large" />
          {this.renderDropdown('bodyAttributes', bodyAttributes, this.isSectionDisabled('bodyAttributes'))}
          <VerticalSpacing type="large" />
          {this.renderDropdown('ethnicGroups', ethnicGroups, this.isSectionDisabled('ethnicGroups'))}
          <VerticalSpacing type="large" />
          {this.renderDropdown('professions', professions, this.isSectionDisabled('professions'))}
        </CollapsibleContainer>
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { filters } = this.props;

    return (
      <CollapsibleSimple
        title={__t('discovery_creators_filters_creator_details.section_label')}
        contentClassName="discovery-creators-filters-creator-details"
        isExpanded
      >
        {this.renderAgeSlider()}
        <VerticalSpacing type="large" />
        {this.renderGender()}
        <VerticalSpacing type="large" />
        {this.renderLocation('countries', countrySearchToDropdownItems)}
        {
          isEmpty(filters.searchByName) && (
            <React.Fragment>
              <VerticalSpacing type="large" />
              {this.renderExtraFilters()}
            </React.Fragment>
          )
        }
      </CollapsibleSimple>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSearchCities: wrappedCitySearchToDropdownItems(dispatch)
});

export default connect(null, mapDispatchToProps)(DiscoveryCreatorsFiltersCreatorDetails);
